/**
 * N2BriefingsListPage — index of daily briefings at /n2/briefings.
 *
 * Public. Shows the most recent 30 briefings as cards with headline +
 * generated timestamp. Clicking a card navigates to the single-briefing
 * page at /n2/briefings/:date.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { N2Frame } from './N2Frame';
import { palette } from './theme';
import { Eyebrow, PageTitle, PageLede } from './N2Primitives';
import { briefingsService, BriefingSummary, BriefingVariant } from '../services/briefingsService';

export const N2BriefingsListPage: React.FC = () => {
  const { token } = useAuth();
  const [variant, setVariant] = useState<BriefingVariant>('eod');
  const [briefings, setBriefings] = useState<BriefingSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    briefingsService.list(variant, 30, token || undefined)
      .then((r) => { if (!cancelled) setBriefings(r.briefings); })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [variant, token]);

  return (
    <N2Frame page="briefings">
      <div style={{ maxWidth: 880, margin: '0 auto', padding: '48px 24px 96px' }}>
        <Eyebrow>Daily briefings</Eyebrow>
        <PageTitle>What the tape did, what tomorrow sets up</PageTitle>
        <PageLede>
          Twice a day — a morning setup and an end-of-day recap — synthesizing every regime flip,
          alert, and macro move into one read. Today plus the last two days are free to read;
          the archive and cross-asset analysis are Premier.
        </PageLede>

        <div style={{
          display: 'flex', gap: 8, marginTop: 32, marginBottom: 16,
          borderBottom: `1px solid ${palette.line}`, paddingBottom: 12,
        }}>
          <VariantTab active={variant === 'eod'} onClick={() => setVariant('eod')}>End of day</VariantTab>
          <VariantTab active={variant === 'morning'} onClick={() => setVariant('morning')}>Morning</VariantTab>
        </div>

        {loading && <div style={{ color: palette.ink3, fontSize: 14 }}>Loading briefings…</div>}
        {error && <div style={{ color: palette.negative, fontSize: 14 }}>{error}</div>}
        {!loading && !error && briefings.length === 0 && (
          <div style={{
            padding: '32px', background: palette.paper2, border: `1px solid ${palette.line}`,
            borderRadius: 8, textAlign: 'center', color: palette.ink3,
          }}>
            No {variant === 'eod' ? 'end-of-day' : 'morning'} briefings yet. Check back after the next scheduled fire.
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
          {briefings.map((b) => (
            <BriefingCard key={`${b.briefingDate}#${b.variant}`} briefing={b} />
          ))}
        </div>
      </div>
    </N2Frame>
  );
};

const VariantTab: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
  active, onClick, children,
}) => (
  <button
    onClick={onClick}
    style={{
      padding: '8px 14px', border: 'none', background: 'transparent',
      cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: active ? 600 : 400,
      color: active ? palette.ink : palette.ink3,
      borderBottom: active ? `2px solid ${palette.gold}` : '2px solid transparent',
      marginBottom: -13,
    }}
  >
    {children}
  </button>
);

const BriefingCard: React.FC<{ briefing: BriefingSummary }> = ({ briefing }) => (
  <Link
    to={`/n2/briefings/${briefing.briefingDate}/${briefing.variant}`}
    style={{
      display: 'block', textDecoration: 'none', color: 'inherit',
      padding: '18px 20px', border: `1px solid ${palette.line}`, borderRadius: 8,
      background: palette.paper, transition: 'border-color 120ms',
    }}
    onMouseOver={(e) => (e.currentTarget.style.borderColor = palette.gold)}
    onMouseOut={(e) => (e.currentTarget.style.borderColor = palette.line)}
  >
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6,
    }}>
      <div style={{ fontFamily: 'var(--serif)', fontSize: 18, fontWeight: 500, color: palette.ink }}>
        {formatDate(briefing.briefingDate)}
      </div>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.05, color: palette.ink3 }}>
        {briefing.variant === 'eod' ? 'End of day' : 'Morning'}
      </div>
    </div>
    <div style={{ color: palette.ink2, fontSize: 14, lineHeight: 1.4 }}>
      {briefing.headline}
    </div>
  </Link>
);

function formatDate(iso: string): string {
  // 'YYYY-MM-DD' -> 'Mon, Jul 27 2026'
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
}

export default N2BriefingsListPage;
